import React from 'react';
import {
  Tabs,
  Button,
  Input,
  Select,
  Checkbox,
  TimePicker,
  Image,
  Card,
  Space,
  Row,
  Col
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { TabPane } = Tabs;
const { Option } = Select;

// 设备选项选择器组件
const DeviceOptionSelect: React.FC<{
  value?: string;
  onChange?: (value: string) => void;
  label: string;
}> = ({ value, onChange, label }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: 'block', marginBottom: 8 }}>{label}</label>
    <Select value={value} onChange={onChange} style={{ width: '100%' }}>
      <Option value="open">打开</Option>
      <Option value="close">关闭</Option>
      <Option value="enforced">强制打开(不可关闭)</Option>
      <Option value="disabled">强制关闭(不可打开)</Option>
      <Option value="nocontrol">不控制</Option>
    </Select>
  </div>
);

interface StrategyVisualEditorProps {
  data: any;
  onChange: (data: any) => void;
  generateGuid: () => string;
}

const StrategyVisualEditor: React.FC<StrategyVisualEditorProps> = ({
  data,
  onChange,
  generateGuid
}) => {
  // 确保数据结构完整
  const safeData = React.useMemo(() => ({
    strategies: {
      queryPolicyInterval: data?.strategies?.queryPolicyInterval || '600',
      appAllowance: data?.strategies?.appAllowance || [],
      appControl: data?.strategies?.appControl || [],
      deviceConfig: data?.strategies?.deviceConfig || [],
      network: data?.strategies?.network || [],
      appHidden: data?.strategies?.appHidden || []
    },
    policies: data?.policies || []
  }), [data]);

  const updateData = (newData: any) => {
    onChange({ ...newData });
  };

  // 应用禁用策略组
  const renderAppAllowanceTab = () => (
    <div style={{ padding: '16px 0' }}>
      {safeData.strategies.appAllowance.map((policy: any, index: number) => (
        <Card
          key={policy.id}
          size="small"
          style={{ marginBottom: 16, backgroundColor: '#f5f5f5' }}
          title={`策略 ${index + 1}`}
          extra={
            <Button
              danger
              size="small"
              onClick={() => {
                const newData = { ...safeData };
                newData.strategies.appAllowance.splice(index, 1);
                updateData(newData);
              }}
            >
              删除当前应用策略
            </Button>
          }
        >
          <div style={{ marginBottom: 16 }}>
            <label>策略名称:</label>
            <Input
              value={policy.name}
              onChange={(e) => {
                const newData = { ...safeData };
                newData.strategies.appAllowance[index].name = e.target.value;
                updateData(newData);
              }}
              style={{ marginTop: 8 }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label>黑白名单:</label>
            <Select
              value={policy.controlType}
              onChange={(value) => {
                const newData = { ...safeData };
                newData.strategies.appAllowance[index].controlType = value;
                updateData(newData);
              }}
              style={{ width: '100%', marginTop: 8 }}
            >
              <Option value="blacklist">黑名单</Option>
              <Option value="whitelist">白名单</Option>
            </Select>
          </div>

          <div>
            <label>App应用:</label>
            {policy.apps.map((app: string, appIndex: number) => (
              <div key={appIndex} style={{ marginTop: 8 }}>
                <Row gutter={8}>
                  <Col span={20}>
                    <Input
                      value={app}
                      onChange={(e) => {
                        const newData = { ...safeData };
                        newData.strategies.appAllowance[index].apps[appIndex] = e.target.value;
                        updateData(newData);
                      }}
                      placeholder="请输入应用包名"
                    />
                  </Col>
                  <Col span={4}>
                    <Button
                      danger
                      onClick={() => {
                        const newData = { ...safeData };
                        newData.strategies.appAllowance[index].apps.splice(appIndex, 1);
                        updateData(newData);
                      }}
                    >
                      删除
                    </Button>
                  </Col>
                </Row>
              </div>
            ))}
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => {
                const newData = { ...safeData };
                newData.strategies.appAllowance[index].apps.push('');
                updateData(newData);
              }}
              style={{ marginTop: 8, width: '100%' }}
            >
              添加App应用配置
            </Button>
          </div>
        </Card>
      ))}
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => {
          const newData = { ...safeData };
          newData.strategies.appAllowance.push({
            id: generateGuid(),
            name: '',
            controlType: 'blacklist',
            apps: []
          });
          updateData(newData);
        }}
      >
        增加应用禁用策略
      </Button>
    </div>
  );

  // APP控制策略组
  const renderAppControlTab = () => (
    <div style={{ padding: '16px 0' }}>
      {safeData.strategies.appControl.map((policy: any, index: number) => (
        <Card
          key={policy.id}
          size="small"
          style={{ marginBottom: 16, backgroundColor: '#f5f5f5' }}
          title={`策略 ${index + 1}`}
          extra={
            <Button
              danger
              size="small"
              onClick={() => {
                const newData = { ...safeData };
                newData.strategies.appControl.splice(index, 1);
                updateData(newData);
              }}
            >
              删除当前控制策略
            </Button>
          }
        >
          <div style={{ marginBottom: 16 }}>
            <label>策略名称:</label>
            <Input
              value={policy.name}
              onChange={(e) => {
                const newData = { ...safeData };
                newData.strategies.appControl[index].name = e.target.value;
                updateData(newData);
              }}
              style={{ marginTop: 8 }}
            />
          </div>

          <div>
            <label>App应用:</label>
            {policy.apps.map((app: any, appIndex: number) => (
              <Card
                key={appIndex}
                size="small"
                style={{ marginTop: 8, backgroundColor: '#e6f7ff' }}
                extra={
                  <Button
                    danger
                    size="small"
                    onClick={() => {
                      const newData = { ...safeData };
                      newData.strategies.appControl[index].apps.splice(appIndex, 1);
                      updateData(newData);
                    }}
                  >
                    删除
                  </Button>
                }
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                      <label>应用名称:</label>
                      <Input
                        value={app.applicationName || ''}
                        onChange={(e) => {
                          const newData = { ...safeData };
                          newData.strategies.appControl[index].apps[appIndex].applicationName = e.target.value;
                          updateData(newData);
                        }}
                        style={{ marginTop: 4 }}
                      />
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                      <label>包名:</label>
                      <Input
                        value={app.packageName}
                        onChange={(e) => {
                          const newData = { ...safeData };
                          newData.strategies.appControl[index].apps[appIndex].packageName = e.target.value;
                          updateData(newData);
                        }}
                        style={{ marginTop: 4 }}
                      />
                    </div>
                  </Col>
                </Row>

                {app.applicationIcon && (
                  <div style={{ marginBottom: 8 }}>
                    <label>应用图标:</label>
                    <div style={{ marginTop: 4 }}>
                      <Image src={app.applicationIcon} width={64} height={64} />
                    </div>
                  </div>
                )}

                <Row gutter={16}>
                  <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                      <label>版本号:</label>
                      <Input
                        value={app.versionCode}
                        onChange={(e) => {
                          const newData = { ...safeData };
                          newData.strategies.appControl[index].apps[appIndex].versionCode = e.target.value;
                          updateData(newData);
                        }}
                        style={{ marginTop: 4 }}
                      />
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                      <label>每日使用分钟:</label>
                      <Input
                        value={app.allowedMinutesPerDay}
                        onChange={(e) => {
                          const newData = { ...safeData };
                          newData.strategies.appControl[index].apps[appIndex].allowedMinutesPerDay = e.target.value;
                          updateData(newData);
                        }}
                        style={{ marginTop: 4 }}
                      />
                    </div>
                  </Col>
                </Row>

                <div style={{ marginBottom: 8 }}>
                  <label>下载链接:</label>
                  <Input
                    value={app.installUrl}
                    onChange={(e) => {
                      const newData = { ...safeData };
                      newData.strategies.appControl[index].apps[appIndex].installUrl = e.target.value;
                      updateData(newData);
                    }}
                    style={{ marginTop: 4 }}
                  />
                </div>

                <div style={{ marginBottom: 8 }}>
                  <label>安装其他应用:</label>
                  <Select
                    value={app.installOtherAppsAllowed}
                    onChange={(value) => {
                      const newData = { ...safeData };
                      newData.strategies.appControl[index].apps[appIndex].installOtherAppsAllowed = value;
                      updateData(newData);
                    }}
                    style={{ width: '100%', marginTop: 4 }}
                  >
                    <Option value="allowed">允许</Option>
                    <Option value="notallowed">不允许</Option>
                    <Option value="nocontrol">不控制</Option>
                  </Select>
                </div>

                <div style={{ marginTop: 12 }}>
                  <Row gutter={[16, 8]}>
                    <Col span={12}>
                      <Checkbox
                        checked={app.installAllowed}
                        onChange={(e) => {
                          const newData = { ...safeData };
                          newData.strategies.appControl[index].apps[appIndex].installAllowed = e.target.checked;
                          updateData(newData);
                        }}
                      >
                        允许安装
                      </Checkbox>
                    </Col>
                    <Col span={12}>
                      <Checkbox
                        checked={app.autoInstall}
                        onChange={(e) => {
                          const newData = { ...safeData };
                          newData.strategies.appControl[index].apps[appIndex].autoInstall = e.target.checked;
                          updateData(newData);
                        }}
                      >
                        自动安装
                      </Checkbox>
                    </Col>
                    <Col span={12}>
                      <Checkbox
                        checked={app.upgradeAllowed}
                        onChange={(e) => {
                          const newData = { ...safeData };
                          newData.strategies.appControl[index].apps[appIndex].upgradeAllowed = e.target.checked;
                          updateData(newData);
                        }}
                      >
                        允许升级
                      </Checkbox>
                    </Col>
                    <Col span={12}>
                      <Checkbox
                        checked={app.uninstallAllowed}
                        onChange={(e) => {
                          const newData = { ...safeData };
                          newData.strategies.appControl[index].apps[appIndex].uninstallAllowed = e.target.checked;
                          updateData(newData);
                        }}
                      >
                        允许卸载
                      </Checkbox>
                    </Col>
                    <Col span={12}>
                      <Checkbox
                        checked={app.autoGrantPermissions}
                        onChange={(e) => {
                          const newData = { ...safeData };
                          newData.strategies.appControl[index].apps[appIndex].autoGrantPermissions = e.target.checked;
                          updateData(newData);
                        }}
                      >
                        自动授权
                      </Checkbox>
                    </Col>
                    <Col span={12}>
                      <Checkbox
                        checked={app.autoUpgrade}
                        onChange={(e) => {
                          const newData = { ...safeData };
                          newData.strategies.appControl[index].apps[appIndex].autoUpgrade = e.target.checked;
                          updateData(newData);
                        }}
                      >
                        自动升级
                      </Checkbox>
                    </Col>
                  </Row>
                </div>
              </Card>
            ))}
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => {
                const newData = { ...safeData };
                newData.strategies.appControl[index].apps.push({
                  packageName: '',
                  versionCode: '',
                  installUrl: '',
                  allowedMinutesPerDay: '0',
                  installOtherAppsAllowed: 'nocontrol',
                  installAllowed: false,
                  autoInstall: false,
                  upgradeAllowed: false,
                  uninstallAllowed: false,
                  autoGrantPermissions: false,
                  autoUpgrade: false
                });
                updateData(newData);
              }}
              style={{ marginTop: 8, width: '100%' }}
            >
              添加App应用配置
            </Button>
          </div>
        </Card>
      ))}
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => {
          const newData = { ...safeData };
          newData.strategies.appControl.push({
            id: generateGuid(),
            name: '',
            apps: []
          });
          updateData(newData);
        }}
      >
        增加APP控制策略
      </Button>
    </div>
  );

  // 设备配置策略组
  const renderDeviceConfigTab = () => (
    <div style={{ padding: '16px 0' }}>
      {safeData.strategies.deviceConfig.map((policy: any, index: number) => (
        <Card
          key={policy.id}
          size="small"
          style={{ marginBottom: 16, backgroundColor: '#f5f5f5' }}
          title={`策略 ${index + 1}`}
          extra={
            <Button
              danger
              size="small"
              onClick={() => {
                const newData = { ...safeData };
                newData.strategies.deviceConfig.splice(index, 1);
                updateData(newData);
              }}
            >
              删除当前设备策略
            </Button>
          }
        >
          <div style={{ marginBottom: 16 }}>
            <label>策略名称:</label>
            <Input
              value={policy.name}
              onChange={(e) => {
                const newData = { ...safeData };
                newData.strategies.deviceConfig[index].name = e.target.value;
                updateData(newData);
              }}
              style={{ marginTop: 8 }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <Checkbox
              checked={policy.globalLock}
              onChange={(e) => {
                const newData = { ...safeData };
                newData.strategies.deviceConfig[index].globalLock = e.target.checked;
                updateData(newData);
              }}
            >
              锁定设备
            </Checkbox>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label>桌面Launcher:</label>
            <Input
              value={policy.currentLauncher || ''}
              onChange={(e) => {
                const newData = { ...safeData };
                newData.strategies.deviceConfig[index].currentLauncher = e.target.value;
                updateData(newData);
              }}
              placeholder="如要使用自定义桌面Launcher，请在此输入包名"
              style={{ marginTop: 8 }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label>应用保持:</label>
            <Input
              value={policy.foregroundApp || ''}
              onChange={(e) => {
                const newData = { ...safeData };
                newData.strategies.deviceConfig[index].foregroundApp = e.target.value;
                updateData(newData);
              }}
              placeholder="如有要打开并不允许切换出去的APP，请在此输入包名"
              style={{ marginTop: 8 }}
            />
          </div>

          <Row gutter={16}>
            <Col span={12}>
              <DeviceOptionSelect
                label="Wifi"
                value={policy.wifi}
                onChange={(value) => {
                  const newData = { ...safeData };
                  newData.strategies.deviceConfig[index].wifi = value;
                  updateData(newData);
                }}
              />
            </Col>
            <Col span={12}>
              <DeviceOptionSelect
                label="移动数据"
                value={policy.mobileData}
                onChange={(value) => {
                  const newData = { ...safeData };
                  newData.strategies.deviceConfig[index].mobileData = value;
                  updateData(newData);
                }}
              />
            </Col>
            <Col span={12}>
              <DeviceOptionSelect
                label="蓝牙传输"
                value={policy.bluetoothData}
                onChange={(value) => {
                  const newData = { ...safeData };
                  newData.strategies.deviceConfig[index].bluetoothData = value;
                  updateData(newData);
                }}
              />
            </Col>
            <Col span={12}>
              <DeviceOptionSelect
                label="摄像拍照"
                value={policy.camera}
                onChange={(value) => {
                  const newData = { ...safeData };
                  newData.strategies.deviceConfig[index].camera = value;
                  updateData(newData);
                }}
              />
            </Col>
            <Col span={12}>
              <DeviceOptionSelect
                label="USB调试"
                value={policy.usbDebug}
                onChange={(value) => {
                  const newData = { ...safeData };
                  newData.strategies.deviceConfig[index].usbDebug = value;
                  updateData(newData);
                }}
              />
            </Col>
            <Col span={12}>
              <DeviceOptionSelect
                label="开发模式"
                value={policy.developerMode}
                onChange={(value) => {
                  const newData = { ...safeData };
                  newData.strategies.deviceConfig[index].developerMode = value;
                  updateData(newData);
                }}
              />
            </Col>
            <Col span={12}>
              <DeviceOptionSelect
                label="USB传输"
                value={policy.usbDataTransfer}
                onChange={(value) => {
                  const newData = { ...safeData };
                  newData.strategies.deviceConfig[index].usbDataTransfer = value;
                  updateData(newData);
                }}
              />
            </Col>
            <Col span={12}>
              <DeviceOptionSelect
                label="SD卡"
                value={policy.sdcard}
                onChange={(value) => {
                  const newData = { ...safeData };
                  newData.strategies.deviceConfig[index].sdcard = value;
                  updateData(newData);
                }}
              />
            </Col>
            <Col span={12}>
              <DeviceOptionSelect
                label="强制静音"
                value={policy.silentMode}
                onChange={(value) => {
                  const newData = { ...safeData };
                  newData.strategies.deviceConfig[index].silentMode = value;
                  updateData(newData);
                }}
              />
            </Col>
            <Col span={12}>
              <DeviceOptionSelect
                label="允许定位"
                value={policy.location}
                onChange={(value) => {
                  const newData = { ...safeData };
                  newData.strategies.deviceConfig[index].location = value;
                  updateData(newData);
                }}
              />
            </Col>
            <Col span={12}>
              <DeviceOptionSelect
                label="允许截图"
                value={policy.screenshot}
                onChange={(value) => {
                  const newData = { ...safeData };
                  newData.strategies.deviceConfig[index].screenshot = value;
                  updateData(newData);
                }}
              />
            </Col>
            <Col span={12}>
              <DeviceOptionSelect
                label="恢复出厂"
                value={policy.factoryReset}
                onChange={(value) => {
                  const newData = { ...safeData };
                  newData.strategies.deviceConfig[index].factoryReset = value;
                  updateData(newData);
                }}
              />
            </Col>
            <Col span={12}>
              <DeviceOptionSelect
                label="安全模式"
                value={policy.safeMode}
                onChange={(value) => {
                  const newData = { ...safeData };
                  newData.strategies.deviceConfig[index].safeMode = value;
                  updateData(newData);
                }}
              />
            </Col>
            <Col span={12}>
              <DeviceOptionSelect
                label="护眼模式"
                value={policy.eyeProtection}
                onChange={(value) => {
                  const newData = { ...safeData };
                  newData.strategies.deviceConfig[index].eyeProtection = value;
                  updateData(newData);
                }}
              />
            </Col>
            <Col span={12}>
              <DeviceOptionSelect
                label="网络共享"
                value={policy.networkTethering}
                onChange={(value) => {
                  const newData = { ...safeData };
                  newData.strategies.deviceConfig[index].networkTethering = value;
                  updateData(newData);
                }}
              />
            </Col>
            <Col span={12}>
              <DeviceOptionSelect
                label="禁止安装新应用"
                value={policy.blockNewPackageInstall}
                onChange={(value) => {
                  const newData = { ...safeData };
                  newData.strategies.deviceConfig[index].blockNewPackageInstall = value;
                  updateData(newData);
                }}
              />
            </Col>
          </Row>
        </Card>
      ))}
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => {
          const newData = { ...safeData };
          newData.strategies.deviceConfig.push({
            id: generateGuid(),
            name: '',
            globalLock: false,
            wifi: '',
            mobileData: '',
            bluetoothData: '',
            camera: '',
            usbDebug: 'nocontrol',
            developerMode: 'nocontrol',
            usbDataTransfer: 'nocontrol',
            sdcard: 'nocontrol',
            silentMode: '',
            location: '',
            screenshot: '',
            factoryReset: '',
            safeMode: '',
            eyeProtection: '',
            networkTethering: '',
            blockNewPackageInstall: 'nocontrol',
            wallpaper: ''
          });
          updateData(newData);
        }}
      >
        增加设备配置策略
      </Button>
    </div>
  );

  // 网络访问策略组  
  const renderNetworkTab = () => (
    <div style={{ padding: '16px 0' }}>
      {safeData.strategies.network.map((policy: any, policyIndex: number) => (
        <Card
          key={policy.id}
          size="small"
          style={{ marginBottom: 16, backgroundColor: '#f5f5f5' }}
          title={`策略 ${policyIndex + 1}`}
          extra={
            <Button
              danger
              size="small"
              onClick={() => {
                const newData = { ...safeData };
                newData.strategies.network.splice(policyIndex, 1);
                updateData(newData);
              }}
            >
              删除策略配置
            </Button>
          }
        >
          <div style={{ marginBottom: 16 }}>
            <label>策略名称:</label>
            <Input
              value={policy.name}
              onChange={(e) => {
                const newData = { ...safeData };
                newData.strategies.network[policyIndex].name = e.target.value;
                updateData(newData);
              }}
              style={{ marginTop: 8 }}
            />
          </div>

          <div>
            <label>策略配置:</label>
            {policy.config.map((config: any, configIndex: number) => (
              <Card
                key={configIndex}
                size="small"
                style={{ marginTop: 8, backgroundColor: '#e6f7ff' }}
              >
                <div style={{ marginBottom: 12 }}>
                  <label>策略运用到 (all表示全局，其它用逗号分隔包名):</label>
                  <Input
                    value={config.applyTo}
                    onChange={(e) => {
                      const newData = { ...safeData };
                      newData.strategies.network[policyIndex].config[configIndex].applyTo = e.target.value;
                      updateData(newData);
                    }}
                    style={{ marginTop: 4 }}
                  />
                </div>

                <div>
                  <label>规则集合:</label>
                  {config.rules.map((rule: any, ruleIndex: number) => (
                    <Card
                      key={ruleIndex}
                      size="small"
                      style={{ marginTop: 8, backgroundColor: '#fffbe6' }}
                    >
                      <div style={{ marginBottom: 8 }}>
                        <label>规则类型:</label>
                        <Select
                          value={rule.type}
                          onChange={(value) => {
                            const newData = { ...safeData };
                            newData.strategies.network[policyIndex].config[configIndex].rules[ruleIndex].type = value;
                            updateData(newData);
                          }}
                          style={{ width: '100%', marginTop: 4 }}
                        >
                          <Option value="blacklist">黑名单</Option>
                          <Option value="whitelist">白名单</Option>
                        </Select>
                      </div>

                      <div>
                        <label>站点集合 (比如*.baidu.com，211.103.#.#):</label>
                        {rule.sites.map((site: string, siteIndex: number) => (
                          <Row key={siteIndex} gutter={8} style={{ marginTop: 4 }}>
                            <Col span={20}>
                              <Input
                                value={site}
                                onChange={(e) => {
                                  const newData = { ...safeData };
                                  newData.strategies.network[policyIndex].config[configIndex].rules[ruleIndex].sites[siteIndex] = e.target.value;
                                  updateData(newData);
                                }}
                              />
                            </Col>
                            <Col span={4}>
                              <Button
                                danger
                                onClick={() => {
                                  const newData = { ...safeData };
                                  newData.strategies.network[policyIndex].config[configIndex].rules[ruleIndex].sites.splice(siteIndex, 1);
                                  updateData(newData);
                                }}
                              >
                                删除
                              </Button>
                            </Col>
                          </Row>
                        ))}
                        <Button
                          type="dashed"
                          size="small"
                          onClick={() => {
                            const newData = { ...safeData };
                            newData.strategies.network[policyIndex].config[configIndex].rules[ruleIndex].sites.push('');
                            updateData(newData);
                          }}
                          style={{ marginTop: 8, width: '100%' }}
                        >
                          添加站点
                        </Button>
                      </div>
                      <Button
                        danger
                        size="small"
                        onClick={() => {
                          const newData = { ...safeData };
                          newData.strategies.network[policyIndex].config[configIndex].rules.splice(ruleIndex, 1);
                          updateData(newData);
                        }}
                        style={{ marginTop: 8 }}
                      >
                        删除规则
                      </Button>
                    </Card>
                  ))}
                  <Button
                    type="dashed"
                    size="small"
                    onClick={() => {
                      const newData = { ...safeData };
                      newData.strategies.network[policyIndex].config[configIndex].rules.push({
                        type: 'blacklist',
                        sites: []
                      });
                      updateData(newData);
                    }}
                    style={{ marginTop: 8, width: '100%' }}
                  >
                    添加规则
                  </Button>
                </div>
                <Button
                  danger
                  size="small"
                  onClick={() => {
                    const newData = { ...safeData };
                    newData.strategies.network[policyIndex].config.splice(configIndex, 1);
                    updateData(newData);
                  }}
                  style={{ marginTop: 12 }}
                >
                  删除配置
                </Button>
              </Card>
            ))}
            <Button
              type="dashed"
              onClick={() => {
                const newData = { ...safeData };
                newData.strategies.network[policyIndex].config.push({
                  applyTo: '',
                  rules: []
                });
                updateData(newData);
              }}
              style={{ marginTop: 8, width: '100%' }}
            >
              添加配置
            </Button>
          </div>
        </Card>
      ))}
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => {
          const newData = { ...safeData };
          newData.strategies.network.push({
            id: generateGuid(),
            name: '',
            config: []
          });
          updateData(newData);
        }}
      >
        增加网络访问策略
      </Button>
    </div>
  );

  // 应用隐藏策略组
  const renderAppHiddenTab = () => (
    <div style={{ padding: '16px 0' }}>
      {safeData.strategies.appHidden.map((policy: any, index: number) => (
        <Card
          key={policy.id}
          size="small"
          style={{ marginBottom: 16, backgroundColor: '#f5f5f5' }}
          title={`策略 ${index + 1}`}
          extra={
            <Button
              danger
              size="small"
              onClick={() => {
                const newData = { ...safeData };
                newData.strategies.appHidden.splice(index, 1);
                updateData(newData);
              }}
            >
              删除当前隐藏策略
            </Button>
          }
        >
          <div style={{ marginBottom: 16 }}>
            <label>策略名称:</label>
            <Input
              value={policy.name}
              onChange={(e) => {
                const newData = { ...safeData };
                newData.strategies.appHidden[index].name = e.target.value;
                updateData(newData);
              }}
              style={{ marginTop: 8 }}
            />
          </div>

          <div>
            <label>隐藏的应用包名列表:</label>
            {policy.apps.map((packageName: string, appIndex: number) => (
              <div key={appIndex} style={{ marginTop: 8 }}>
                <Row gutter={8}>
                  <Col span={20}>
                    <Input
                      value={packageName}
                      onChange={(e) => {
                        const newData = { ...safeData };
                        newData.strategies.appHidden[index].apps[appIndex] = e.target.value;
                        updateData(newData);
                      }}
                      placeholder="请输入应用包名，如：com.example.app"
                    />
                  </Col>
                  <Col span={4}>
                    <Button
                      danger
                      onClick={() => {
                        const newData = { ...safeData };
                        newData.strategies.appHidden[index].apps.splice(appIndex, 1);
                        updateData(newData);
                      }}
                    >
                      删除
                    </Button>
                  </Col>
                </Row>
              </div>
            ))}
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => {
                const newData = { ...safeData };
                newData.strategies.appHidden[index].apps.push('');
                updateData(newData);
              }}
              style={{ marginTop: 8, width: '100%' }}
            >
              添加应用包名
            </Button>
          </div>
        </Card>
      ))}
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => {
          const newData = { ...safeData };
          newData.strategies.appHidden.push({
            id: generateGuid(),
            name: '',
            apps: []
          });
          updateData(newData);
        }}
      >
        增加应用隐藏策略
      </Button>
    </div>
  );

  // 管控措施组
  const renderPoliciesTab = () => (
    <div style={{ padding: '16px 0' }}>
      {safeData.policies.map((policy: any, policyIndex: number) => (
        <Card
          key={policy.id}
          size="small"
          style={{ marginBottom: 16, backgroundColor: '#f5f5f5' }}
          title={`措施 ${policyIndex + 1}`}
          extra={
            <Button
              danger
              size="small"
              onClick={() => {
                const newData = { ...safeData };
                newData.policies.splice(policyIndex, 1);
                updateData(newData);
              }}
            >
              删除当前管控措施
            </Button>
          }
        >
          <div style={{ marginBottom: 16 }}>
            <label>措施名称:</label>
            <Input
              value={policy.name}
              onChange={(e) => {
                const newData = { ...safeData };
                newData.policies[policyIndex].name = e.target.value;
                updateData(newData);
              }}
              style={{ marginTop: 8 }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label>措施运用到每周指定日期:</label>
            <div style={{ marginTop: 8 }}>
              <Checkbox.Group
                value={policy.appliedWeekdays}
                onChange={(values) => {
                  const newData = { ...safeData };
                  newData.policies[policyIndex].appliedWeekdays = values.sort((a, b) => a - b);
                  updateData(newData);
                }}
              >
                <Row>
                  <Col span={8}><Checkbox value={1}>星期一</Checkbox></Col>
                  <Col span={8}><Checkbox value={2}>星期二</Checkbox></Col>
                  <Col span={8}><Checkbox value={3}>星期三</Checkbox></Col>
                  <Col span={8}><Checkbox value={4}>星期四</Checkbox></Col>
                  <Col span={8}><Checkbox value={5}>星期五</Checkbox></Col>
                  <Col span={8}><Checkbox value={6}>星期六</Checkbox></Col>
                  <Col span={8}><Checkbox value={7}>星期日</Checkbox></Col>
                </Row>
              </Checkbox.Group>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label>措施运用到勾选日期的时间段:</label>
            {policy.appliedTimeRanges.map((timeRange: any, timeIndex: number) => {
              const isAllDay = timeRange.allDay || (timeRange.startTime === '00:00' && timeRange.endTime === '24:00');
              return (
                <Card
                  key={timeIndex}
                  size="small"
                  style={{ marginTop: 8, backgroundColor: '#e6f7ff' }}
                >
                  <div style={{ marginBottom: 8 }}>
                    <Checkbox
                      checked={isAllDay}
                      onChange={(e) => {
                        const newData = { ...safeData };
                        const checked = e.target.checked;
                        const targetRange = newData.policies[policyIndex].appliedTimeRanges[timeIndex];
                        if (checked) {
                          targetRange.startTime = '00:00';
                          targetRange.endTime = '24:00';
                        } else {
                          targetRange.startTime = '';
                          targetRange.endTime = '';
                        }
                        delete targetRange.allDay;
                        updateData(newData);
                      }}
                    >
                      全天管控
                    </Checkbox>
                  </div>

                  {!isAllDay && (
                    <Row gutter={8}>
                      <Col span={10}>
                        <label>开始时间:</label>
                        <TimePicker
                          value={timeRange.startTime ? dayjs(timeRange.startTime, 'HH:mm') : null}
                          onChange={(time) => {
                            const newData = { ...safeData };
                            newData.policies[policyIndex].appliedTimeRanges[timeIndex].startTime = time ? time.format('HH:mm') : '';
                            updateData(newData);
                          }}
                          format="HH:mm"
                          style={{ width: '100%', marginTop: 4 }}
                        />
                      </Col>
                      <Col span={10}>
                        <label>结束时间:</label>
                        <TimePicker
                          value={timeRange.endTime ? dayjs(timeRange.endTime, 'HH:mm') : null}
                          onChange={(time) => {
                            const newData = { ...safeData };
                            newData.policies[policyIndex].appliedTimeRanges[timeIndex].endTime = time ? time.format('HH:mm') : '';
                            updateData(newData);
                          }}
                          format="HH:mm"
                          style={{ width: '100%', marginTop: 4 }}
                        />
                      </Col>
                      <Col span={4}>
                        <Button
                          danger
                          onClick={() => {
                            const newData = { ...safeData };
                            newData.policies[policyIndex].appliedTimeRanges.splice(timeIndex, 1);
                            updateData(newData);
                          }}
                          style={{ marginTop: 20 }}
                        >
                          删除
                        </Button>
                      </Col>
                    </Row>
                  )}
                  {isAllDay && (
                    <Button
                      danger
                      onClick={() => {
                        const newData = { ...safeData };
                        newData.policies[policyIndex].appliedTimeRanges.splice(timeIndex, 1);
                        updateData(newData);
                      }}
                    >
                      删除
                    </Button>
                  )}
                </Card>
              );
            })}
            <Button
              type="dashed"
              onClick={() => {
                const newData = { ...safeData };
                newData.policies[policyIndex].appliedTimeRanges.push({
                  startTime: '',
                  endTime: ''
                });
                updateData(newData);
              }}
              style={{ marginTop: 8, width: '100%' }}
            >
              添加管控时间段
            </Button>
          </div>

          <div style={{ backgroundColor: 'rgba(0,0,0,0.02)', padding: 16, borderRadius: 4 }}>
            <label style={{ fontWeight: 'bold' }}>请选择本措施执行的各策略</label>
            
            <div style={{ marginTop: 12 }}>
              <label>应用禁用策略:</label>
              <Select
                value={policy.strategies.appAllowance}
                onChange={(value) => {
                  const newData = { ...safeData };
                  newData.policies[policyIndex].strategies.appAllowance = value;
                  updateData(newData);
                }}
                style={{ width: '100%', marginTop: 4 }}
                allowClear
                placeholder="-- 请选择 --"
              >
                {safeData.strategies.appAllowance.map((item: any) => (
                  <Option key={item.id} value={item.id}>{item.name}</Option>
                ))}
              </Select>
            </div>

            <div style={{ marginTop: 12 }}>
              <label>应用控制策略:</label>
              <Select
                value={policy.strategies.appControl}
                onChange={(value) => {
                  const newData = { ...safeData };
                  newData.policies[policyIndex].strategies.appControl = value;
                  updateData(newData);
                }}
                style={{ width: '100%', marginTop: 4 }}
                allowClear
                placeholder="-- 请选择 --"
              >
                {safeData.strategies.appControl.map((item: any) => (
                  <Option key={item.id} value={item.id}>{item.name}</Option>
                ))}
              </Select>
            </div>

            <div style={{ marginTop: 12 }}>
              <label>设备配置策略:</label>
              <Select
                value={policy.strategies.deviceConfig}
                onChange={(value) => {
                  const newData = { ...safeData };
                  newData.policies[policyIndex].strategies.deviceConfig = value;
                  updateData(newData);
                }}
                style={{ width: '100%', marginTop: 4 }}
                allowClear
                placeholder="-- 请选择 --"
              >
                {safeData.strategies.deviceConfig.map((item: any) => (
                  <Option key={item.id} value={item.id}>{item.name}</Option>
                ))}
              </Select>
            </div>

            <div style={{ marginTop: 12 }}>
              <label>网络访问策略:</label>
              <Select
                value={policy.strategies.network}
                onChange={(value) => {
                  const newData = { ...safeData };
                  newData.policies[policyIndex].strategies.network = value;
                  updateData(newData);
                }}
                style={{ width: '100%', marginTop: 4 }}
                allowClear
                placeholder="-- 请选择 --"
              >
                {safeData.strategies.network.map((item: any) => (
                  <Option key={item.id} value={item.id}>{item.name}</Option>
                ))}
              </Select>
            </div>

            <div style={{ marginTop: 12 }}>
              <label>应用隐藏策略:</label>
              <Select
                value={policy.strategies.appHidden}
                onChange={(value) => {
                  const newData = { ...safeData };
                  newData.policies[policyIndex].strategies.appHidden = value;
                  updateData(newData);
                }}
                style={{ width: '100%', marginTop: 4 }}
                allowClear
                placeholder="-- 请选择 --"
              >
                {safeData.strategies.appHidden.map((item: any) => (
                  <Option key={item.id} value={item.id}>{item.name}</Option>
                ))}
              </Select>
            </div>
          </div>
        </Card>
      ))}
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => {
          const newData = { ...safeData };
          newData.policies.push({
            id: generateGuid(),
            name: '',
            appliedWeekdays: [],
            appliedTimeRanges: [],
            strategies: {
              appAllowance: '',
              appControl: '',
              appHidden: '',
              network: '',
              deviceConfig: ''
            }
          });
          updateData(newData);
        }}
      >
        增加管控措施
      </Button>
    </div>
  );

  return (
    <div>
      {/* 全局配置 */}
      <Card size="small" style={{ marginBottom: 16 }} title="全局配置">
        <div>
          <label>策略查询间隔（秒）：</label>
          <Input
            type="number"
            value={safeData.strategies.queryPolicyInterval}
            onChange={(e) => {
              const newData = { ...safeData };
              newData.strategies.queryPolicyInterval = e.target.value;
              updateData(newData);
            }}
            style={{ width: 200, marginTop: 8 }}
            placeholder="默认 600 秒"
          />
        </div>
      </Card>

      <Tabs defaultActiveKey="appAllowance">
        <TabPane tab={`应用禁用策略组 (${safeData.strategies.appAllowance.length}条)`} key="appAllowance">
          {renderAppAllowanceTab()}
        </TabPane>
        <TabPane tab={`APP控制策略组 (${safeData.strategies.appControl.length}条)`} key="appControl">
          {renderAppControlTab()}
        </TabPane>
        <TabPane tab={`应用隐藏策略组 (${safeData.strategies.appHidden.length}条)`} key="appHidden">
          {renderAppHiddenTab()}
        </TabPane>
        <TabPane tab={`设备配置策略组 (${safeData.strategies.deviceConfig.length}条)`} key="deviceConfig">
          {renderDeviceConfigTab()}
        </TabPane>
        <TabPane tab={`网络访问策略组 (${safeData.strategies.network.length}条)`} key="network">
          {renderNetworkTab()}
        </TabPane>
        <TabPane tab={`管控措施组 (${safeData.policies.length}条)`} key="policies">
          {renderPoliciesTab()}
        </TabPane>
      </Tabs>
    </div>
  );
};

export default StrategyVisualEditor;
